import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AlertErrorCircle } from '@/assets/icons'
import { Modal, ModalContent, ModalFooterWithActions } from '@/components/modal'
import InputFieldFloatingLabel from '@/components/input-field-floating-label/InputFieldFloatingLabel'
import { kUserFilesFileNameMaxLength, kUserFilesFolderNameMaxLength } from '@/user_files/limits'
import { hasInvalidUserFilesNameCharacter, kInvalidUserFilesNameMessage } from '@/user_files/utils/name-validation'
import type { RenameItem } from '@/user_files/drive/hooks/useRenameDialog'
import { splitFilename } from '@/user_files/utils/filename'

interface RenameItemDialogProps {
  /** Whether the dialog is open. */
  open: boolean
  /** The item to rename. */
  item: RenameItem | null
  /** Whether any user-files batched operation is in progress. */
  isRenaming: boolean
  /** Inline error when open/confirm is invalid or blocked; null when there is no error. */
  error: string | null
  /** Names already taken in the current folder (excluding the item's own current name). */
  occupiedNames: ReadonlySet<string>
  /** Closes the dialog and clears captured IDs and errors. */
  onClose: () => void
  /** Renames, clears selection, and closes the dialog. */
  onConfirm: (newName: string) => void
}

function buildSchema(item: RenameItem | null, occupiedNames: ReadonlySet<string>, ext: string) {
  const maxLength = item?.type === 'file' ? kUserFilesFileNameMaxLength : kUserFilesFolderNameMaxLength
  const effectiveMax = maxLength - ext.length
  return z.object({
    name: z
      .string()
      .trim()
      .min(1, 'Name is required')
      .max(effectiveMax, `Name must be ${effectiveMax} characters or less`)
      .refine((val) => !hasInvalidUserFilesNameCharacter(val), kInvalidUserFilesNameMessage)
      .refine(
        (val) => !occupiedNames.has(val + ext),
        item?.type === 'file' ? 'A file with this name already exists in this folder' : 'A folder with this name already exists in this folder'
      ),
  })
}

type RenameForm = { name: string }

/**
 * RenameItemDialog
 *
 * Shared modal for renaming files and folders.
 * Uses react-hook-form + zod for validation. Errors are shown inline.
 */
export function RenameItemDialog({ open, item, isRenaming, error, occupiedNames, onClose, onConfirm }: RenameItemDialogProps) {
  const { base, ext } = useMemo(() => {
    if (item?.type !== 'file') return { base: item?.name ?? '', ext: '' }
    return splitFilename(item.name)
  }, [item])

  const schema = useMemo(() => buildSchema(item, occupiedNames, ext), [item, occupiedNames, ext])

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setFocus,
  } = useForm<RenameForm>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { name: base },
  })

  // Reset form and focus the input when the dialog opens.
  // RAF defers the focus until after Radix's onOpenAutoFocus (which focuses the modal container).
  useEffect(() => {
    if (!open || !item) return
    reset({ name: base })
    const raf = requestAnimationFrame(() => setFocus('name'))
    return () => cancelAnimationFrame(raf)
  }, [open, item, base, reset, setFocus])

  const onSubmit = (data: RenameForm) => {
    onConfirm(data.name + ext)
  }

  const handleSave = () => {
    if (!isDirty) {
      onClose()
      return
    }
    handleSubmit(onSubmit)()
  }

  if (!item) return null

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <ModalContent
        title={item?.type === 'file' ? 'Rename file' : 'Rename folder'}
        width="sm"
        height="max-sm"
        footer={
          <ModalFooterWithActions
            tertiaryText={
              <>
                <span className="text-red-700">*</span>
                {' Required field'}
              </>
            }
            secondaryAction={{ label: 'Cancel', onClick: onClose }}
            primaryAction={{
              label: 'Save',
              loading: isRenaming,
              onClick: handleSave,
            }}
          />
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            handleSave()
          }}
        >
          <InputFieldFloatingLabel
            id={item?.type === 'file' ? 'rename-file-name' : 'rename-folder-name'}
            label="Name"
            required
            error={errors.name?.message}
            registration={register('name')}
          />

          {error && (
            <p className="p-2 mt-3 flex items-center gap-1 text-xs text-red-800 rounded bg-red-50">
              <AlertErrorCircle className="shrink-0 w-3.5 h-3.5" />
              {error}
            </p>
          )}
        </form>
      </ModalContent>
    </Modal>
  )
}
